import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { TID } from "@/lib/constants";
import { toast } from "sonner";
import ImageInput from "@/components/ImageInput";

const CATEGORY_OPTIONS = ["GS-1", "GS-2", "GS-3", "GS-4", "Prelims", "PIB", "Ethics"];
const BLANK = {
  headline: "",
  summary_60: "",
  image_url: "https://images.unsplash.com/photo-1526779259212-939e64788e3c?auto=format&fit=crop&w=1600&q=70",
  category: "GS-3",
  state_tag: "",
  pib_tag: false,
  ethics_tag: false,
  data_tag: false,
  pyq_tag: "",
  prelims_pointers_text: "",
  diagram_url: "",
  mains_utility: "",
  full_brief: "",
  source: "",
  source_url: "",
  quiz: { question: "", options: ["", "", "", ""], correct_index: 0, explanation: "" },
  tags: [],
  hasQuiz: false,
};

function wordCount(s) { return (s || "").trim().split(/\s+/).filter(Boolean).length; }

export default function AdminCardEditor() {
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const list = await adminApi.listCards();
        const c = (list.cards || []).find((x) => x.card_id === id);
        if (c) {
          setForm({
            ...BLANK,
            ...c,
            prelims_pointers_text: (c.prelims_pointers || []).join("\n"),
            full_brief: c.full_brief || "",
            quiz: c.quiz || BLANK.quiz,
            hasQuiz: !!c.quiz,
            tags: c.tags || [],
          });
          setTagsInput((c.tags || []).join(", "));
        }
      } catch { toast.error("Failed to load card"); }
    })();
  }, [id, editing]);

  const wc = useMemo(() => wordCount(form.summary_60), [form.summary_60]);
  const briefWc = useMemo(() => wordCount(form.full_brief || ""), [form.full_brief]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.headline.trim()) return toast.error("Headline required");
    if (wc < 40 || wc > 90) return toast.error(`Summary must be 40–90 words (currently ${wc}).`);
    if (!form.image_url) return toast.error("Feature Image required. Paste a URL or upload one.");
    setBusy(true);
    try {
      const pointers = (form.prelims_pointers_text || "")
        .split("\n")
        .map((p) => p.replace(/^[\s*\-\u2022]+/, "").trim())
        .filter(Boolean);
      const payload = {
        headline: form.headline.trim(),
        summary_60: form.summary_60.trim(),
        image_url: form.image_url.trim(),
        category: form.category,
        state_tag: form.state_tag || null,
        pib_tag: !!form.pib_tag,
        ethics_tag: !!form.ethics_tag,
        data_tag: !!form.data_tag,
        pyq_tag: form.pyq_tag || null,
        prelims_pointers: pointers,
        diagram_url: form.diagram_url || null,
        mains_utility: form.mains_utility || "",
        full_brief: form.full_brief?.trim() || null,
        source: form.source || "",
        source_url: form.source_url || null,
        quiz: form.hasQuiz && form.quiz.question.trim() ? {
          question: form.quiz.question.trim(),
          options: form.quiz.options.map((o) => o.trim()).filter(Boolean),
          correct_index: Math.max(0, Math.min(3, Number(form.quiz.correct_index) || 0)),
          explanation: form.quiz.explanation || null,
        } : null,
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing) await adminApi.updateCard(id, payload);
      else await adminApi.createCard(payload);
      toast.success(editing ? "Card updated" : "Card created");
      navigate("/studio");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <form onSubmit={save} className="mx-auto w-full max-w-[900px] px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-[#F8FAFC]">{editing ? "Edit Card" : "New Card"}</h1>
          <Link to="/studio" className="text-xs text-[#94A3B8] hover:text-[#E85A2A] transition-colors">← Back</Link>
        </div>

        <div className="mt-5 rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 sm:p-6">
          <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Headline</label>
          <input required value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
            className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />

          <label className="mt-4 flex items-center justify-between text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
            <span>Summary <span className="normal-case tracking-normal text-[#94A3B8]">(40&ndash;90 words · sweet spot 55&ndash;65)</span></span>
            <span data-testid={TID.adminWordCounter} className={`${(wc>=55 && wc<=65) ? "text-[#2F6F4F]" : (wc>=40 && wc<=90) ? "text-[#F2BD68]" : "text-[#B42318]"} normal-case tracking-normal`}>{wc} words</span>
          </label>
          <textarea required value={form.summary_60} onChange={(e) => setForm({ ...form, summary_60: e.target.value })}
            className="mt-2 min-h-[140px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" style={{ lineHeight: 1.6 }} />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]">
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">State Tag</label>
              <input value={form.state_tag || ""} onChange={(e) => setForm({ ...form, state_tag: e.target.value })} placeholder="e.g. HPSC"
                className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm text-[#F8FAFC]">
              <input type="checkbox" checked={form.pib_tag} onChange={(e) => setForm({ ...form, pib_tag: e.target.checked })} />
              PIB Filtered
            </label>
            <label className="flex items-center gap-2 text-sm text-[#F8FAFC]">
              <input type="checkbox" checked={form.data_tag} onChange={(e) => setForm({ ...form, data_tag: e.target.checked })} />
              Data (stats/reports)
            </label>
            <label className="flex items-center gap-2 text-sm text-[#F8FAFC]">
              <input type="checkbox" checked={form.ethics_tag} onChange={(e) => setForm({ ...form, ethics_tag: e.target.checked })} />
              Ethics &amp; Essay
            </label>
          </div>

          <div className="mt-4">
            <ImageInput
              label="Feature Image"
              value={form.image_url}
              onChange={(v) => setForm({ ...form, image_url: v })}
              aspect={16 / 9}
              helper="This is the big image users see on the front of the card. Cards render at 16:9 — the crop tool lets you pick exactly what's shown. Paste a URL, or upload from your computer and frame it."
              testId="admin-feature-image"
            />
          </div>

          <div className="mt-4">
            <ImageInput
              label="Diagram Image (optional)"
              value={form.diagram_url || ""}
              onChange={(v) => setForm({ ...form, diagram_url: v || null })}
              helper="Optional. Shows on the back of the card — for diagrams, no forced aspect ratio (keep original). Great for maps, flowcharts, or infographics students can copy in exams."
              testId="admin-diagram-image"
            />
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 sm:p-6">
          <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Prelims Pointers</label>
          <textarea
            value={form.prelims_pointers_text}
            onChange={(e) => setForm({ ...form, prelims_pointers_text: e.target.value })}
            placeholder={"One point per line.\ne.g.\nArticle 280 establishes Finance Commission every 5 years.\nChairman: Arvind Panagariya.\nReport due Oct 2025."}
            data-testid="admin-prelims-pointers-textarea"
            className="mt-2 min-h-[120px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] font-mono focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            style={{ lineHeight: 1.7 }}
          />
          <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
            One point per line. Leading bullets (<code>-</code>, <code>*</code>, <code>&bull;</code>) are stripped automatically.
          </p>

          <label className="mt-5 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Mains Utility</label>
          <textarea value={form.mains_utility} onChange={(e) => setForm({ ...form, mains_utility: e.target.value })}
            className="mt-2 min-h-[80px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" style={{ lineHeight: 1.6 }} />

          <label className="mt-5 flex items-center justify-between text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
            <span>Full Brief <span className="normal-case tracking-normal text-[#94A3B8]">(optional &middot; deeper reading on the back of the card)</span></span>
            {form.full_brief ? <span className="normal-case tracking-normal text-[#94A3B8]">{briefWc} words</span> : null}
          </label>
          <textarea
            value={form.full_brief}
            onChange={(e) => setForm({ ...form, full_brief: e.target.value })}
            data-testid="admin-full-brief-textarea"
            placeholder="For long, meaty topics — write the deeper 200–400 word brief here. It only appears on the BACK of the card, not the front, so the Reels feel of the feed stays intact."
            className="mt-2 min-h-[140px] w-full rounded-2xl border border-[#26334D] bg-[#1A2232] p-4 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            style={{ lineHeight: 1.6 }}
          />
          {briefWc > 500 && (
            <p className="mt-1 text-xs text-[#F2BD68]" style={{ lineHeight: 1.6 }}>Tip: {briefWc} words is quite long — students may skim. Consider tightening under 500.</p>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">PYQ Tag</label>
              <input value={form.pyq_tag || ""} onChange={(e) => setForm({ ...form, pyq_tag: e.target.value })} placeholder="e.g. Prelims 2022"
                className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Source</label>
              <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. The Hindu / PIB"
                className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
            </div>
          </div>

          <label className="mt-4 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Tags (comma separated)</label>
          <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="#GS3, #Economy, #HPSC"
            className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
        </div>

        <div className="mt-4 rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 sm:p-6">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#F8FAFC]">
            <input type="checkbox" checked={form.hasQuiz} onChange={(e) => setForm({ ...form, hasQuiz: e.target.checked })} />
            Include Micro-Quiz for this card
          </label>
          {form.hasQuiz && (
            <div className="mt-3 space-y-3">
              <input value={form.quiz.question} onChange={(e) => setForm({ ...form, quiz: { ...form.quiz, question: e.target.value } })} placeholder="Quiz question"
                className="h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
              {[0,1,2,3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="correct_index"
                    checked={Number(form.quiz.correct_index) === i}
                    onChange={() => setForm({ ...form, quiz: { ...form.quiz, correct_index: i } })}
                  />
                  <input
                    value={form.quiz.options[i] || ""}
                    onChange={(e) => {
                      const opts = [...form.quiz.options];
                      opts[i] = e.target.value;
                      setForm({ ...form, quiz: { ...form.quiz, options: opts } });
                    }}
                    placeholder={`Option ${i + 1}`}
                    className="h-11 flex-1 rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
                  />
                </div>
              ))}
              <input value={form.quiz.explanation || ""} onChange={(e) => setForm({ ...form, quiz: { ...form.quiz, explanation: e.target.value } })} placeholder="Short explanation (shown after submit)"
                className="h-11 w-full rounded-xl border border-[#26334D] bg-[#1A2232] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2A]" />
            </div>
          )}
        </div>

        <button data-testid={TID.adminSave} disabled={busy} type="submit"
          className="mt-5 w-full sm:w-auto rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-5 py-3 text-sm font-semibold hover:bg-[#C94A1D] transition-colors disabled:opacity-50">
          {busy ? "Saving…" : (editing ? "Update Card" : "Publish Card")}
        </button>
      </form>
    </div>
  );
}
